// InvoiceHistory.tsx
import React, { Dispatch, SetStateAction, useState } from "react";
import { Invoice } from "./types";
import { Edit, Eye, Trash } from "lucide-react";
import { DataTable } from "../../../DataTable/DataTable";
import toast from "react-hot-toast";
import { InverntroyService } from "../../../../services/api/inventroy.service";
import InvoiceDetails from "./InvoiceHistoryDetails/invoiceHistoryDetails";
import defaultAvatar from "../../../../assets/images/default_avatar.jpg";

interface InvoiceHistoryProps {
  searchTerm?: string;
  setShowInvoiceDetails:()=> void | Dispatch<SetStateAction<boolean>>;
  showInvoiceDetails:boolean | string;
  clinicId?:string
}

const InvoiceHistory: React.FC<InvoiceHistoryProps> = ({ searchTerm,setShowInvoiceDetails,showInvoiceDetails,clinicId }) => {

  const [isLoading, setIsLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalItemsCount, setTotalItemsCount] = React.useState(0);
  const [invoiceData, setInvoiceData] = React.useState([]);
  const [selectedInvoice, setSelectedInvoice] = React.useState<string | null>(null);

  const loadInvoiceData = async (page: number, pageSize: number,searchTerm?:string) => {
    try {
      setIsLoading(true);
      let response=null;
      if(clinicId){
        response=await InverntroyService.getInvoiceHistoryForClinic(page,pageSize,clinicId,searchTerm);
      }else{
        response = await InverntroyService.getInvoiceHistory(
        page,
        pageSize,
        searchTerm
      );
      }
      if (response.data.length > 0) {
        setInvoiceData(response.data);
      } else {
        toast.error(response.message);
        setInvoiceData([]);
      }
      setTotalItemsCount(response.pagination.totalItemsCount);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error loading invoice data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    loadInvoiceData(currentPage, pageSize,searchTerm);
  }, [currentPage, pageSize,searchTerm]);

  const columns = [
  {
    key: "InvoiceNumber",
    title: "Invoice Number",
    width: "w-[200px]",
    render: (item: Invoice) => (
      <button
        type="button"
        onClick={() => {
          setSelectedInvoice(item.invoice_number);
          setShowInvoiceDetails(true);
        }}
        className="text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:underline text-left cursor-pointer"
        title="View invoice details"
      >
        {item.invoice_number || "N/A"}
      </button>
    ),
  },
  {
    key: "InvoiceDate",
    title: "Invoice Date",
    width: "w-[200px]",
    render: (item: Invoice) => (
      <div className="text-sm font-medium">
        {item.invoice_date.split("T")[0] || "N/A"}
      </div>
    ),
  },
  {
    key: "InvoiceAmount",
    title: "Invoice Amount",
    width: "w-[200px]",
    render: (item: Invoice) => (
      <div className="text-sm font-medium">
        {Number(item.invoice_amount).toFixed(2) || "N/A"}
      </div>
    ),
  },
  {
    key: "PaymentStatus",
    title: "Payment Status",
    width: "w-[160px]",
    render: (item: Invoice) => {
      const status = (
        item.payment_status ||
        item.paymentStatus ||
        "pending"
      ).toLowerCase();
      const isCompleted = status === "completed" || status === "success";
      return (
        <span
          className={`px-2.5 py-1 inline-flex text-xs font-semibold rounded-full capitalize ${
            isCompleted
              ? "bg-green-100 text-green-800"
              : "bg-amber-100 text-amber-800"
          }`}
        >
          {item.payment_status || item.paymentStatus || "Pending"}
        </span>
      );
    },
  },
  {
    key: "wholesaler",
    title: "Wholesaler",
    width: "w-[200px]",
    render: (item: Invoice) => (
      <div className="text-sm font-medium">
        {item.wholesaler || "N/A"}
      </div>
    ),
  },
  // Conditionally add Clinic Name column
  ...(!clinicId
    ? [
        {
          key: "ClinicName",
          title: "Clinic Name",
          width: "w-[200px]",
          render: (item: Invoice) => (
            <div className="flex items-center space-x-2">
              {item.clinicDetails.profileImage ? (
                <img
                  src={item.clinicDetails.profileImage}
                  alt={item.clinicDetails.name}
                  className="w-8 h-8 rounded"
                />
              ) : (
                <img src={defaultAvatar} alt="User" className="w-6 h-6" />
              )}
              <div
                className="text-sm font-medium line-clamp-2 cursor-default"
                title={item.clinicDetails.name}
              >
                {item?.clinicDetails.name || "N/A"}
              </div>
            </div>
          ),
        },
      ]
    : []),
  {
    key: "NoOfProducts",
    title: "No. of Products",
    width: "w-[200px]",
    render: (item: Invoice) => (
      <div className="text-sm font-medium">
        {item.number_of_products || "N/A"}
      </div>
    ),
  },
  {
    key: "Actions",
    title: "Actions",
    width: "w-[200px]",
    render: (item: Invoice) => (
      <div className="flex space-x-4">
        <button
          className="text-yellow-500 hover:text-yellow-700"
          onClick={() => {
            setSelectedInvoice(item.invoice_number);
            setShowInvoiceDetails(true);
          }}
        >
          <Eye className="w-5 h-5" />
        </button>
      </div>
    ),
  },
];


  return (
    <div className="relative ">
      {showInvoiceDetails ? (
        <InvoiceDetails
          invoiceNumber={selectedInvoice}
          onBack={() => {
            setShowInvoiceDetails(false);
            loadInvoiceData(currentPage, pageSize, searchTerm);
          }}
          onRefresh={() => {
            loadInvoiceData(currentPage, pageSize, searchTerm);
          }}
          clinicId={clinicId}
        />
      ) : (
        <DataTable
          data={invoiceData}
          columns={columns}
          isLoading={isLoading}
          customFilters={[]}
          pagination={{
            currentPage,
            setCurrentPage,
            setTotalPages,
            totalPages,
            totalItemsCount,
            pageSize,
            setPageSize,
          }}
        />
      )}
    </div>
  );
};

export default InvoiceHistory;
